/*
 * Lesson Prep Card · Batch P2.1 · Feb 2026
 * =========================================
 *
 * Mobile-first, glanceable pre-lesson surface. Answers ONE question
 * in 15 seconds: "What do I need to know before I drive away?"
 *
 * Truth-boundary rules:
 *   • Every section renders exactly what the backend authoritative
 *     aggregator returned — nothing is inferred client-side.
 *   • Every empty state uses the backend's honest wording.
 *   • Never claims delivery / receipt / read.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, MapPin, Clock, Target, Eye, Layers } from "lucide-react";
import { GuidanceBanner } from "../components/GuidanceBanner";
import { useTruthState } from "../hooks/useTruthState";
import { TruthReceipt } from "../components/TruthReceipt";
import PupilLessonChoices from "../components/instructor/PupilLessonChoices";

const API = process.env.REACT_APP_BACKEND_URL || "";

const OBJECTIVE_SOURCE_LABEL = {
  instructor:                "Instructor",
  active_objective:          "Learning objective",
  shared_learning:           "Current learning evidence",
  previous_recommendation:   "From last lesson",
  none:                      "Not set",
  pupil_choice:              "Pupil’s choice",
  instructor_adjustment:     "Agreed adjustment",
};

const WATCH_KIND_LABEL = {
  upcoming_test:   "Test coming up",
  instructor_note: "Instructor note",
  confidence:      "Confidence",
  recurring_gap:   "Recurring gap",
};

const EVIDENCE_KIND_LABEL = {
  lesson_analysis:      "Lesson analysis",
  instructor_note:      "Instructor note",
  voice_reflection:     "Voice reflection",
  instructor_outcome:   "Instructor outcome",
  pupil_homework:       "Pupil homework",
  pupil_understanding:  "Pupil understanding",
};

function fmtTime(t) {
  if (!t) return "";
  const parts = String(t).split(":");
  return parts.length >= 2 ? `${parts[0]}:${parts[1]}` : t;
}

function Section({ icon: Icon, title, empty, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-slate-500" />
        <h2 className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</h2>
      </div>
      {empty ? (
        <p className="text-sm text-slate-400 italic">{empty}</p>
      ) : (
        <div className="space-y-2">{children}</div>
      )}
    </section>
  );
}

export default function LessonPrepCard() {
  const { lessonId } = useParams();
  const [card, setCard] = useState(null);
  const [preparation, setPreparation] = useState(null);
  // Batch P1 · Lesson Truth-State — `viewState` still owns the
  // authoritative-response render forks (loading spinner vs 404
  // panel vs 403 panel vs rendered card). These are DOMAIN outcomes
  // returned by the backend, not transient action errors. Transient
  // network / 500 failures are surfaced through the canonical
  // TruthReceipt below, exposing Retry + Dismiss (Truth Doctrine).
  const [viewState, setViewState] = useState("loading");
  const truth = useTruthState();

  const load = useCallback(async () => {
    try {
      await truth.run(
        async () => {
          const r = await axios.get(
            `${API}/api/lesson-events/${lessonId}/prep-card`,
            { withCredentials: true },
          );
          // Read this exact lesson's saved choices with instructor ownership
          // enforced by the preparation endpoint. Do not infer them from the
          // diary objective or treat a failed read as an empty response.
          let savedPreparation = null;
          try {
            const saved = await axios.get(
              `${API}/api/run-my-day/lessons/${lessonId}/preparation`,
              { withCredentials: true },
            );
            savedPreparation = saved.data;
          } catch (error) {
            if (error?.response?.status !== 404) throw error;
          }
          setPreparation(savedPreparation);
          setCard(r.data);
          setViewState("ok");
          return r.data;
        },
        { failedMessage: "Couldn't load prep card." },
      );
    } catch (err) {
      // 404 / 403 are authoritative backend responses — render the
      // dedicated panel and leave the TruthReceipt idle. Everything
      // else is a genuine failure, kept visible by TruthReceipt.
      const status = err?.response?.status;
      if (status === 404) {
        setViewState("not_found");
        truth.setState(null);
      } else if (status === 403) {
        setViewState("forbidden");
        truth.setState(null);
      } else {
        setViewState("error");
      }
    }
  }, [lessonId, truth]);

  useEffect(() => { load(); }, [load]);

  // Completed loads auto-clear so the prep card stays calm.
  useEffect(() => {
    if (truth.state === "completed") {
      const t = setTimeout(() => truth.setState(null), 400);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [truth]);

  if (viewState === "loading") {
    return (
      <div data-testid="prep-card-loading" className="min-h-dvh grid place-items-center bg-slate-50 dark:bg-slate-950">
        <p className="text-sm text-slate-500">Loading prep card…</p>
      </div>
    );
  }
  if (viewState === "not_found") {
    return (
      <div data-testid="prep-card-not-found" className="min-h-dvh grid place-items-center bg-slate-50 dark:bg-slate-950">
        <p className="text-sm text-rose-600">Lesson not found.</p>
      </div>
    );
  }
  if (viewState === "forbidden") {
    return (
      <div data-testid="prep-card-forbidden" className="min-h-dvh grid place-items-center bg-slate-50 dark:bg-slate-950">
        <p className="text-sm text-rose-600">You don&apos;t have access to this lesson.</p>
      </div>
    );
  }
  if (viewState !== "ok" || !card) {
    // Transient error — TruthReceipt owns the message + Retry.
    return (
      <div data-testid="prep-card-error" className="min-h-dvh grid place-items-center bg-slate-50 dark:bg-slate-950 px-4">
        <div className="w-full max-w-sm">
          <TruthReceipt
            state={truth.state}
            message={truth.message}
            onRetry={load}
            onDismiss={() => truth.setState(null)}
          />
        </div>
      </div>
    );
  }

  const { lesson, previous, objective: previousObjective, watch, evidence } = card;
  const objective = preparation?.lesson_plan?.desired_focus
    ? { text: preparation.lesson_plan.desired_focus, source: preparation.plan_source }
    : previousObjective;

  return (
    <div data-testid="prep-card" className="min-h-dvh bg-slate-50 dark:bg-slate-950">
      <div className="max-w-md mx-auto p-4 space-y-3">

        {/* Back */}
        <Link
          to="/instructor"
          data-testid="prep-card-back"
          className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
        >
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>

        {/* Contextual guidance — silent unless the OS has a nudge. */}
        <GuidanceBanner hideWhenQuiet />

        {/* Learner header — the anchor. Large typography, high contrast. */}
        <header
          data-testid="prep-card-header"
          className="rounded-2xl bg-slate-900 dark:bg-slate-800 text-white p-5 shadow-sm"
        >
          <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Next lesson</p>
          <h1 data-testid="prep-card-pupil-name" className="text-3xl font-black leading-tight">
            {lesson.pupil_name}
          </h1>
          <div className="mt-3 flex items-center gap-3 text-sm text-slate-300">
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              <span data-testid="prep-card-time">{fmtTime(lesson.start_time)}–{fmtTime(lesson.end_time)}</span>
            </span>
            {lesson.duration_minutes && (
              <span data-testid="prep-card-duration">· {lesson.duration_minutes}m</span>
            )}
          </div>
          {lesson.pickup_location && (
            <div className="mt-1.5 flex items-center gap-1 text-sm text-slate-300">
              <MapPin className="w-3.5 h-3.5" />
              <span data-testid="prep-card-pickup">{lesson.pickup_location}</span>
            </div>
          )}
        </header>

        <section className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
          {preparation?.pupil_proposal ? (
            <PupilLessonChoices
              proposal={preparation.pupil_proposal}
              submittedAt={preparation.pupil_submitted_at}
              late={preparation.pupil_response_late}
              adjusted={preparation.plan_source === "instructor_adjustment"}
            />
          ) : (
            <p className="text-sm text-slate-500">No pupil choices have been shared for this lesson yet.</p>
          )}
        </section>

        {/* Today's objective — hero item. */}
        <section
          data-testid="prep-card-objective"
          className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30 p-4"
        >
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-emerald-700 dark:text-emerald-400" />
            <h2 className="text-xs font-bold uppercase tracking-wide text-emerald-800 dark:text-emerald-300">
              Today · {OBJECTIVE_SOURCE_LABEL[objective.source] || "Objective"}
            </h2>
          </div>
          <p
            data-testid="prep-card-objective-text"
            className={`text-lg font-semibold leading-snug ${
              objective.source === "none"
                ? "text-slate-400 italic"
                : "text-slate-900 dark:text-white"
            }`}
          >
            {objective.text}
          </p>
        </section>

        {/* Previous lesson */}
        <Section
          icon={Layers}
          title="Previous lesson"
          empty={!previous?.present ? previous?.empty_state : null}
        >
          {previous?.present && (
            <div data-testid="prep-card-previous">
              <p className="text-sm text-slate-700 dark:text-slate-200 leading-snug">
                {previous.summary}
              </p>
              <dl className="grid grid-cols-1 gap-1 mt-3 text-xs">
                {previous.went_well && (
                  <div>
                    <dt className="font-semibold text-emerald-700 dark:text-emerald-400">Went well</dt>
                    <dd data-testid="prep-card-went-well" className="text-slate-700 dark:text-slate-200">
                      {previous.went_well}
                    </dd>
                  </div>
                )}
                {previous.biggest_difficulty && (
                  <div>
                    <dt className="font-semibold text-rose-700 dark:text-rose-400">Biggest difficulty</dt>
                    <dd data-testid="prep-card-difficulty" className="text-slate-700 dark:text-slate-200">
                      {previous.biggest_difficulty}
                    </dd>
                  </div>
                )}
              </dl>
              {previous.review_link && (
                <Link
                  to={previous.review_link}
                  data-testid="prep-card-review-link"
                  className="inline-block mt-2 text-xs font-semibold text-emerald-700 dark:text-emerald-400 hover:underline"
                >
                  View full review →
                </Link>
              )}
            </div>
          )}
        </Section>

        {/* Things to watch */}
        <Section
          icon={Eye}
          title="Things to watch"
          empty={watch?.length ? null : "Nothing flagged to watch."}
        >
          <ul data-testid="prep-card-watch-list" className="space-y-1.5">
            {(watch || []).map((w, i) => (
              <li
                key={i}
                data-testid={`prep-card-watch-${i}`}
                className="text-sm text-slate-800 dark:text-slate-100 flex items-start gap-2"
              >
                <span className="text-[10px] font-bold uppercase text-amber-700 dark:text-amber-400 pt-0.5 shrink-0 w-24">
                  {WATCH_KIND_LABEL[w.kind] || w.kind}
                </span>
                <span className="flex-1">{w.text}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Evidence */}
        <Section
          icon={Layers}
          title="Evidence"
          empty={evidence?.length ? null : "No evidence available."}
        >
          <ul data-testid="prep-card-evidence-list" className="space-y-2">
            {(evidence || []).map((e, i) => {
              const inner = (
                <div className="rounded-lg bg-slate-100 dark:bg-slate-800 p-2">
                  <p className="text-[10px] font-bold uppercase text-slate-500">
                    {EVIDENCE_KIND_LABEL[e.kind] || e.kind}
                  </p>
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">{e.title}</p>
                  {e.text && <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">{e.text}</p>}
                </div>
              );
              return (
                <li key={i} data-testid={`prep-card-evidence-${i}`}>
                  {e.link ? <Link to={e.link}>{inner}</Link> : inner}
                </li>
              );
            })}
          </ul>
        </Section>

      </div>
    </div>
  );
}

